import { useEffect, useRef } from "react";
import { syncWeighbridgeTicketsFromAgent } from "../../../services/weighbridge_sync_service";

const DEFAULT_AUTO_SYNC_INTERVAL_MS = 5 * 60 * 1000;
const DEFAULT_INITIAL_DELAY_MS = 5 * 1000;

const AUTO_SYNC_INTERVAL_MS = Math.max(
  60 * 1000,
  Number(process.env.REACT_APP_WEIGHBRIDGE_AUTO_SYNC_INTERVAL_MS) || DEFAULT_AUTO_SYNC_INTERVAL_MS,
);
const AUTO_SYNC_INITIAL_DELAY_MS = Math.max(
  1000,
  Number(process.env.REACT_APP_WEIGHBRIDGE_AUTO_SYNC_INITIAL_DELAY_MS) || DEFAULT_INITIAL_DELAY_MS,
);
const LAST_AUTO_SYNC_ATTEMPT_KEY = "weighbridge_auto_sync_last_attempt_at";

const hasDashboardSession = () => {
  try {
    return Boolean(localStorage.getItem("access") && localStorage.getItem("log_cred"));
  } catch (error) {
    console.error("Could not resolve dashboard session for weighbridge auto sync.", error);
    return false;
  }
};

const getLastAutoSyncAttemptAt = () => {
  try {
    const storedValue = localStorage.getItem(LAST_AUTO_SYNC_ATTEMPT_KEY);
    const parsedValue = Number(storedValue);

    return Number.isFinite(parsedValue) ? parsedValue : 0;
  } catch (error) {
    console.error("Could not read weighbridge auto sync timestamp.", error);
    return 0;
  }
};

const setLastAutoSyncAttemptAt = (timestamp) => {
  try {
    localStorage.setItem(LAST_AUTO_SYNC_ATTEMPT_KEY, String(timestamp));
  } catch (error) {
    console.error("Could not persist weighbridge auto sync timestamp.", error);
  }
};

const WeighbridgeAutoSync = () => {
  const syncInFlightRef = useRef(false);

  useEffect(() => {
    let isCancelled = false;

    const runAutoSync = async ({ bypassThrottle = false } = {}) => {
      if (isCancelled || syncInFlightRef.current || !hasDashboardSession()) {
        return;
      }

      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }

      const now = Date.now();
      const lastAttemptAt = getLastAutoSyncAttemptAt();

      if (!bypassThrottle && now - lastAttemptAt < AUTO_SYNC_INTERVAL_MS) {
        return;
      }

      syncInFlightRef.current = true;
      setLastAutoSyncAttemptAt(now);

      try {
        await syncWeighbridgeTicketsFromAgent();
      } catch (error) {
        console.warn("Background weighbridge sync skipped.", error);
      } finally {
        syncInFlightRef.current = false;
      }
    };

    const initialTimerId = window.setTimeout(() => {
      void runAutoSync();
    }, AUTO_SYNC_INITIAL_DELAY_MS);

    const intervalId = window.setInterval(() => {
      void runAutoSync({ bypassThrottle: true });
    }, AUTO_SYNC_INTERVAL_MS);

    return () => {
      isCancelled = true;
      window.clearTimeout(initialTimerId);
      window.clearInterval(intervalId);
    };
  }, []);

  return null;
};

export default WeighbridgeAutoSync;
